import { useState } from 'react';
import {
  Button,
  Heading,
  ModalBody,
  ModalFooter,
  ModalSplit,
  Paragraph,
  Stack,
} from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { useMountEffect } from '@/hooks/state/use-mount-effect';
import * as styles from '../styles.css';

interface OfferBuilderDowngradeConfirmStepProps {
  campaignsPlural: string;
  onMoveToBuilder: () => Promise<void>;
  /** Omit to hide the "Continue canceling" escape hatch. */
  onContinueCanceling?: () => void;
  onClose: () => void;
  /** Blocks the downgrade for users without account/edit or in a read-only masquerade session. */
  isConfirmDisabled?: boolean;
}

/**
 * The Builder-plan downgrade confirmation: spells out what the switch removes, then applies it.
 * Moving to Builder runs the injected mutation and closes the modal; declining continues the cancel
 * flow.
 */
export function OfferBuilderDowngradeConfirmStep({
  campaignsPlural,
  onMoveToBuilder,
  onContinueCanceling,
  onClose,
  isConfirmDisabled,
}: OfferBuilderDowngradeConfirmStepProps) {
  const { track } = useAnalytics();
  const [isApplying, setIsApplying] = useState(false);

  useMountEffect(() => {
    void track('account', 'builder_downgrade_confirm_viewed', {});
  });

  const handleMoveToBuilder = async () => {
    setIsApplying(true);
    try {
      await onMoveToBuilder();
      void track('account', 'builder_downgrade_accepted', {});
      onClose();
    } catch {
      // The opener surfaces the failure snackbar; keep the modal open so the user can retry.
    } finally {
      setIsApplying(false);
    }
  };

  const handleContinueCanceling = () => {
    void track('account', 'builder_downgrade_declined', {});
    onContinueCanceling?.();
  };

  return (
    <>
      <ModalSplit className={styles.builderDowngradeSplitContainer}>
        <div className={styles.builderConfirmEyebrowInvert}>Will be removed</div>
        <div className={styles.builderConfirmBigHeading}>You'll lose access to:</div>
        <div className={styles.builderConfirmLossList}>
          <div className={styles.builderConfirmLossItem}>All {campaignsPlural}</div>
          <div className={styles.builderConfirmLossItem}>All dynamic segments</div>
          <div className={styles.builderConfirmLossItem}>Custom objects</div>
          <div className={styles.builderConfirmLossItem}>Tech stack integrations</div>
        </div>
      </ModalSplit>
      <ModalBody mb="150" px="300">
        <Stack gap="150">
          <Stack gap="050">
            <div className={styles.builderConfirmEyebrow}>Before you switch</div>
            <Heading level="3">Here's what to expect</Heading>
          </Stack>
          <Paragraph>
            Your switch to Builder is immediate. The features listed will be removed and can't be restored.
          </Paragraph>
          <Paragraph>
            Everything else moves with you. Builder is pay-as-you-go, so you only pay when you send.
          </Paragraph>
        </Stack>
      </ModalBody>
      <ModalFooter justifyContent="flex-start" px="300">
        <Button
          variant="primary"
          icon="arrow-right"
          iconPosition="trailing"
          isDisabled={isConfirmDisabled}
          isLoading={isApplying}
          onClick={handleMoveToBuilder}
        >
          Move to Builder
        </Button>
        {onContinueCanceling != null ? (
          <Button variant="tertiary" isDisabled={isApplying} onClick={handleContinueCanceling}>
            Continue canceling
          </Button>
        ) : null}
      </ModalFooter>
    </>
  );
}
